import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Stack,
  CircularProgress,
  TextField,
  ListItemText,
  ListItemIcon,
  Menu,
  MenuItem,
  Slide,
  Card,
  Typography,
  Avatar,
  Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChatMessages from "./ChatMessages";
import ChatHeader from "./ChatHeader";
import { English } from "../../constant/EnglishFallback";
import { useDispatch, useSelector } from "react-redux";
import BuildIdEvent from "../../common/BuildIdEvent";
import { BsSend } from "react-icons/bs";
import { SAVE_CHAT_MEDIA_MASSAGES, SEND_CHAT_GROUP_FIREBASE_NOTIFY } from "../../constant/Apipath";
import { Postrequestcall } from "../../request/Postrequest";
import { Fileuploadrequestcall } from "../../request/Fileuploadrequest";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  PhotoLibrary,
  Mic,
  Close,
  Business,
  Person,
  Construction,
  Build
} from "@mui/icons-material";
import { setChatState } from "../../redux/actions/actions";
import ChatAudioRecorder from "./ChatAudioRecorder";

const ChatContainer = ({ updateChatList, messageLoader, setMessageLoader, isGroupListLoading }) => {
  const buildIdEvent = BuildIdEvent();

  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const typeInputRef = useRef(null);

  const { multiLanguageData, loginData, chatState, selectedChat } = useSelector(
    (state) => state?.main
  );
  const LanguageData = multiLanguageData?.Chat || {};
  const {
    TEXT_PLACEHOLDER_MASSAGE = English.Chat.TEXT_PLACEHOLDER_MASSAGE,
    TEXT_RECORD_A_MASSAGE = English.Chat.TEXT_RECORD_A_MASSAGE,
    TEXT_SAVE = English.Chat.TEXT_SAVE,
    TEXT_IMAGES = English.Chat.TEXT_IMAGES,
    TEXT_RECORD_AUDIO = English.Chat.TEXT_RECORD_AUDIO,
    TEXT_VIDEO = English.Chat.TEXT_VIDEO,
    TEXT_SHARE_PROFILE = English.Chat.TEXT_SHARE_PROFILE,
    TEXT_READY = English.Chat.TEXT_READY,
    TEXT_SEND = English.Chat.TEXT_SEND
  } = LanguageData || {};

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [audio, setAudio] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [audioRecorder, setAudioRecorder] = useState(false);

  // Focus input whenever current user changes
  useEffect(() => {
    // Focus input and clear composer state when switching chat groups
    typeInputRef.current?.focus();
    setMessage("");
    setFiles([]);
    setImagePreview("");
    setAudio("");
  }, [selectedChat?.chatGroupId]); // runs every time you switch to a new user

  useEffect(() => {
    if (chatState?.from === "newChat" && chatState?.defaultMessage && !message) {
      setMessage(chatState.defaultMessage);
    }
  }, [chatState?.defaultMessage, chatState?.from, message]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openModal = () => {
    setAudioRecorder(true);
  };

  const removeImage = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileSelection = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = selectedFiles.filter((file) => file.type.startsWith("image/"));
    if (validFiles.length !== selectedFiles.length) {
      alert("Only image files are supported");
    }
    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    const imageFile = validFiles.find((file) => file.type.startsWith("image/"));
    if (imageFile) {
      setImagePreview(URL.createObjectURL(imageFile));
    }
    event.target.value = ""; // Clear the input value to allow re-selection of the same file
  };

  const handleClose = () => {
    dispatch(setChatState(null));
  };

  const handleSendMessage = async () => {
    const textToSend = message?.trim() || chatState?.defaultMessage || "";
    const hasNoContent = !textToSend && !audio && files.length === 0 && !chatState?.messageText;
    if (hasNoContent) {
      alert("No content to send.");
      return;
    }
    setLoading(true);
    try {
      if (textToSend) {
        await onSendMessage(1, textToSend);
      }
      if (imagePreview) {
        await onSendMessage(2);
      }
      if (!textToSend && chatState?.messageLink) {
        await onSendMessage(3);
      }
    } catch (error) {
      console.error("handleSendMessage-> Error sending message: ", error);
      alert("Failed to send message. Please try again.");
    } finally {
      if (chatState?.defaultMessage) {
        dispatch(
          setChatState({
            ...chatState,
            defaultMessage: ""
          })
        );
      }
    }
  };

  const onSendMessage = async (messageType, messageValue, audioLink) => {
    try {
      switch (messageType) {
        case 1:
          await sendTextMessage(messageType, messageValue || message);
          break;
        case 2:
          await sendImageMessage(messageType, files);
          break;
        case 3:
          await sendLinkMessage(messageType, chatState);
          break;
        case 4:
          await sendAudioMessage(messageType, audioLink);
          break;
        default:
          break;
      }
    } catch (error) {
      alert("Failed to send message. Please try again.");
      console.error("onSendMessage-> Error sending message: ", error);
    }
  };

  const onClear = () => {
    // Clear chatState after a slight delay to ensure the message was sent
    setMessage("");
    typeInputRef.current?.focus();
    setFiles([]);
    setImagePreview("");
  };

  // Send Text Message
  const sendTextMessage = async (messageType, messageValue) => {
    const trimmedMessage = (messageValue || "").trim();
    if (trimmedMessage) {
      await sendMessage(messageType, trimmedMessage, trimmedMessage);
    }
  };

  // Send Image Message
  const sendImageMessage = async (messageType, files) => {
    if (files?.length > 0) {
      const addedPictures = files?.filter((file) => file.type.startsWith("image/"));
      for (let i = 0; i < addedPictures?.length; i++) {
        const formData = new FormData();
        formData.append("file", addedPictures[i]);
        const url = await Fileuploadrequestcall(
          SAVE_CHAT_MEDIA_MASSAGES,
          formData,
          loginData?.accessToken
        );
        const picUrl = url?.data?.data;
        await sendMessage(messageType, picUrl[0].fileName, "You have a new image message");
      }
    }
  };

  // Send Link Message
  const sendLinkMessage = async (messageType, chatState) => {
    let getChatstate = { ...chatState };
    if (getChatstate?.messageText) {
      // Clear chatState after sending link message
      dispatch(
        setChatState({
          ...chatState,
          messageLink: "",
          messageText: "",
          from: ""
        })
      );
      await sendMessage(
        messageType,
        getChatstate.messageLink,
        `You have a new message from ${getChatstate?.messageText?.type}`
      );
    }
  };

  // Send Audio Message
  const sendAudioMessage = async (messageType, audio) => {
    await sendMessage(messageType, audio, "You have a new audio message");
  };

  const getChatGroupName = (selectedChat) => {
    const users = [
      { firstName: selectedChat?.groupUserFirstName1 },
      { firstName: selectedChat?.groupUserFirstName2 }
    ].filter((user) => user.firstName);
    const userCount = users.length;
    if (userCount === 0) return "Unknown Group";
    if (userCount === 1) return users[0]?.firstName;
    if (userCount === 2) return `${users[0]?.firstName} and ${users[1]?.firstName}`;
    return `${users[0]?.firstName}, ${users[1]?.firstName} and ${userCount - 2} others`;
  };

  const sendMessage = async (messageType, content, msgBody) => {
    let getMessages = [
      loginData?.userInformation?.UserId,
      loginData?.userInformation?.FirstName,
      loginData?.userInformation?.LastName,
      loginData?.pictureUrl,
      selectedChat?.chatGroupId,
      content,
      messageType,
      loginData?.ChatLanguage
    ];
    const chatGroupName = getChatGroupName(selectedChat);
    try {
      const res = await buildIdEvent.sendMessage(getMessages);
      onClear();
      setLoading(false);
      await sendChatNotification(selectedChat?.chatGroupId, chatGroupName, msgBody);
      return res;
    } catch (error) {
      console.error("sendMessage-> Error sending message: ", error);
      throw error;
    }
  };

  const sendChatNotification = async (chatGroupId, chatGroupName, body) => {
    const obj = {
      ChatGroupId: chatGroupId,
      Notification: {
        ActionUrl: `${import.meta.env.REACT_APP_CONVERSATIONSCREEN}${chatGroupId}`,
        title: chatGroupName,
        Body: body || "You have a new message",
        Tags: {
          chatGroupId: chatGroupId,
          chatGroupName: chatGroupName
        }
      }
    };
    try {
      await Postrequestcall(`${SEND_CHAT_GROUP_FIREBASE_NOTIFY}`, obj, loginData?.accessToken);
    } catch (error) {
      console.error("sendChatNotification-> Error sending chat notification: ", error);
    }
  };

  const getIcon = (type) => {
    if (!type) return <Business />;
    const lowerType = type.toLowerCase();
    if (lowerType.includes("company") || lowerType.includes("manufacture")) {
      return <Business />;
    }
    if (lowerType.includes("trade")) {
      return <Construction />;
    }
    if (lowerType.includes("product") || lowerType.includes("material")) {
      return <Build />;
    }
    return <Business />;
  };

  const renderMenu = () => {
    return (
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            minWidth: 200,
            borderRadius: 2,
            mt: 1,
            "& .MuiMenuItem-root": {
              px: 2,
              py: 1.5,
              borderRadius: 1,
              mx: 0.5,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "action.hover",
                transform: "translateX(4px)"
              }
            },
            "& .MuiListItemIcon-root": {
              minWidth: "40px"
            }
          }
        }}>
        {/* Header */}
        <Box sx={{ px: 2, py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <ListItemText
            primary="Attach Media"
            primaryTypographyProps={{
              variant: "subtitle2",
              fontWeight: 600,
              color: "text.secondary"
            }}
          />
        </Box>
        {/* Images */}
        <MenuItem
          onClick={() => {
            fileInputRef.current.click();
            handleMenuClose();
          }}
          sx={{ mt: 1 }}>
          <ListItemIcon>
            <PhotoLibrary sx={{ color: "#4CAF50", fontSize: 22 }} />
          </ListItemIcon>
          <ListItemText
            primary={TEXT_IMAGES}
            primaryTypographyProps={{
              fontWeight: 500
            }}
            secondary="Photos and images"
            secondaryTypographyProps={{
              fontSize: 12,
              color: "text.secondary"
            }}
          />
        </MenuItem>
        {/* Audio Recording */}
        <MenuItem
          onClick={() => {
            openModal();
            handleMenuClose();
          }}>
          <ListItemIcon>
            <Mic sx={{ color: "#FF5722", fontSize: 22 }} />
          </ListItemIcon>
          <ListItemText
            primary={TEXT_RECORD_AUDIO}
            primaryTypographyProps={{
              fontWeight: 500
            }}
            secondary="Voice message"
            secondaryTypographyProps={{
              fontSize: 12,
              color: "text.secondary"
            }}
          />
        </MenuItem>
        {/* Video */}
        {/* <MenuItem
          onClick={() => {
            alert("upcoming");
            handleMenuClose();
          }}>
          <ListItemIcon>
            <Videocam sx={{ color: "#2196F3", fontSize: 22 }} />
          </ListItemIcon>
          <ListItemText
            primary={TEXT_VIDEO}
            primaryTypographyProps={{
              fontWeight: 500
            }}
            secondary="Video message"
            secondaryTypographyProps={{
              fontSize: 12,
              color: "text.secondary"
            }}
          />
        </MenuItem> */}
      </Menu>
    );
  };

  const renderMessagePreview = () => {
    return (
      <Slide
        direction="down"
        in={true}
        mountOnEnter
        unmountOnExit
        style={{ padding: "16px", minHeight: "120px" }}>
        <Card
          sx={{
            mb: 1,
            p: 2,
            backgroundColor: "background.paper",
            borderColor: "divider",
            position: "relative",
            borderTop: "1px solid #e0e0e0",
            borderRadius: "0px"
          }}>
          {/* Close Button */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "error.50",
              color: "error.main",
              width: 28,
              height: 28,
              "&:hover": {
                backgroundColor: "error.100",
                transform: "scale(1.1)"
              },
              transition: "all 0.2s ease"
            }}
            size="small">
            <Close fontSize="small" />
          </IconButton>
          {/* Preview Label */}
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              mb: 1,
              display: "block"
            }}>
            {TEXT_SHARE_PROFILE}
          </Typography>
          {/* Main Content */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              pr: 4 // Space for close button
            }}>
            {/* Avatar/Image */}
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={chatState?.messageText?.imageUrl}
                sx={{
                  width: 64,
                  height: 64,
                  border: "3px solid",
                  borderColor: "primary.main",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }}>
                {chatState?.messageText?.type === "Manufacture" ||
                chatState?.messageText?.type === "Company" ? (
                  <Business sx={{ fontSize: 28 }} />
                ) : (
                  <Person sx={{ fontSize: 28 }} />
                )}
              </Avatar>
            </Box>
            {/* Profile Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "primary.main",
                  fontWeight: 600,
                  mb: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}>
                {chatState?.messageText?.name}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Chip
                  label={chatState?.messageText?.type}
                  size="small"
                  variant="outlined"
                  icon={getIcon(chatState?.messageText?.type)}
                  sx={{
                    borderColor: "primary.main",
                    color: "primary.main",
                    backgroundColor: "primary.50",
                    fontSize: 12,
                    height: 24
                  }}
                />
              </Box>
            </Box>
            {/* Action Indicator */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5
              }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "success.main",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.5 }
                  },
                  animation: "pulse 2s infinite"
                }}
              />
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10 }}>
                {TEXT_READY}
              </Typography>
            </Box>
          </Box>
          {/* Progress Bar */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 2,
              backgroundColor: "primary.main",
              borderRadius: "0 0 12px 12px",
              "@keyframes slideIn": {
                from: { transform: "scaleX(0)" },
                to: { transform: "scaleX(1)" }
              },
              animation: "slideIn 0.5s ease-out"
            }}
          />
        </Card>
      </Slide>
    );
  };

  const handleChange = useMemo(
    () => (e) => {
      setMessage(e.target.value);
    },
    [] // no dependencies → stable reference
  );

  // ✅ Memoize the TextField itself
  const messageInput = useMemo(
    () => (
      <TextField
        inputRef={typeInputRef}
        variant="outlined"
        fullWidth
        size="small"
        placeholder="Type a message"
        value={message}
        onChange={handleChange}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault(); // prevent new line
            // Only send if there's meaningful content (text, image, files, link or audio)
            const hasContent =
              Boolean(message?.trim()) ||
              Boolean(imagePreview) ||
              files.length > 0 ||
              Boolean(chatState?.messageLink) ||
              Boolean(audio);
            if (!loading && hasContent) {
              handleSendMessage();
            }
          }
        }}
        sx={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          "& .MuiOutlinedInput-root": {
            borderRadius: "5px"
          }
        }}
      />
    ),
    [message, handleChange] // re-mount only if these change
  );

  return (
    <>
      {/* Chat Header */}
      <ChatHeader updateChatList={updateChatList} />
      {/* Chat Communication */}
      <ChatMessages
        key={`${selectedChat?.chatGroupId}-${loginData?.ChatLanguage}`}
        messageLoader={messageLoader}
        setMessageLoader={setMessageLoader}
        isGroupListLoading={isGroupListLoading}
      />
      {/* Image Preview after select */}
      {files.length !== 0 && (
        <Box
          display="flex"
          bgcolor="#fff"
          sx={{
            gap: 1,
            p: 1,
            overflowX: "auto",
            overflowY: "hidden",
            minHeight: files.length ? 100 : 0,
            maxHeight: 110,
            borderTop: "1px solid #e0e0e0",
            boxShadow: files.length ? 1 : 0,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" }
          }}>
          {files.map((picItem, picIndex) => (
            <Box
              key={picIndex}
              sx={{
                position: "relative",
                minWidth: 80,
                width: 80,
                height: 80,
                mr: 1,
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: 2,
                border: "1px solid #eee",
                "&:hover img": { filter: "brightness(0.9)" }
              }}>
              <img
                src={picItem?.id ? picItem?.pictureUrl : URL.createObjectURL(picItem)}
                alt="Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "8px",
                  transition: "filter 0.2s"
                }}
                loading="lazy"
              />
              <IconButton
                onClick={() => removeImage(picIndex)}
                size="small"
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  color: "#fff",
                  backgroundColor: "rgba(0,0,0,0.4)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" }
                }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      {renderMenu()}
      {/* Show Profile details message preview */}
      {chatState?.messageText &&
        chatState?.from !== "newChat" &&
        chatState?.from !== "notification" &&
        renderMessagePreview()}
      {/* Message send input area */}
      <Box sx={{ backgroundColor: "#fff", padding: "0px 10px 10px 10px" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {messageInput}
          <IconButton onClick={handleMenuOpen} sx={{ color: "#919191" }}>
            <AttachFileIcon size={24} />
          </IconButton>
          <IconButton
            onClick={handleSendMessage}
            sx={{
              color: "#fff",
              backgroundColor: "#083DB8",
              "&:hover": {
                backgroundColor: "#00916e"
              }
            }}
            disabled={
              loading ||
              (!message?.trim() && !imagePreview && !files.length && !chatState?.messageLink)
            }>
            {loading ? <CircularProgress size={24} color="inherit" /> : <BsSend size={24} />}
          </IconButton>
        </Stack>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelection}
          accept="image/*"
          multiple
          style={{ display: "none" }}
        />
        <ChatAudioRecorder
          isOpen={audioRecorder}
          TEXT_SAVE={TEXT_SAVE}
          TEXT_SEND={TEXT_SEND}
          TEXT_RECORD_A_MASSAGE={TEXT_RECORD_A_MASSAGE}
          onClose={() => setAudioRecorder(false)}
          sendAudio={(audioLink) => {
            if (audioLink) {
              setAudio(audioLink);
              setAudioRecorder(false);
              onSendMessage(4, audioLink);
            } else {
              console.warn("AudioRecorderModal-> No audio link received.");
            }
          }}
        />
      </Box>
    </>
  );
};

export default ChatContainer;
